import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PhoneRoundedIcon from '@material-ui/icons/PhoneRounded';
import { FormattedMessage, FormattedTime, useIntl } from 'react-intl';

export type ContactPhoneLinkedEventProps = {
  /** Contact display name; falls back to a generic label when the chat has none. */
  contactName?: string;
  /** Linked phone in E.164 format, including the leading `+`. */
  phone: string;
  /** Epoch millis of the event. */
  timestamp?: number;
};

/**
 * Inline timeline item for `CONTACT_PHONE_LINKED` (ENG-1304): the contact shared their phone
 * through a REQUEST_CONTACT_INFO request and it was linked. A system event, not a message bubble.
 */
const ContactPhoneLinkedEvent: React.FC<ContactPhoneLinkedEventProps> = ({
  contactName,
  phone,
  timestamp,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <Box
      className={classes.root}
      mx={2}
      py={1}
      data-testid="contact-phone-linked-event"
    >
      <span className={classes.icon} aria-hidden="true">
        <PhoneRoundedIcon fontSize="inherit" />
      </span>
      <span className={classes.text}>
        <FormattedMessage
          id="ChatEvent.ContactPhoneLinked"
          values={{
            name:
              contactName ||
              intl.formatMessage({
                id: 'ChatEvent.ContactPhoneLinkedDefaultName',
              }),
            phone,
            strong: (chunks: React.ReactNode) => (
              <span className={classes.phone}>{chunks}</span>
            ),
          }}
        />
      </span>
      {timestamp && (
        <span className={classes.time}>
          <FormattedTime value={timestamp} />
        </span>
      )}
    </Box>
  );
};

export default ContactPhoneLinkedEvent;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  icon: {
    flex: '0 0 auto',
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    backgroundColor: theme.timelineEvent.phoneLinkedIconBg,
    color: theme.timelineEvent.phoneLinkedAccent,
  },
  text: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  phone: {
    color: theme.timelineEvent.phoneLinkedAccent,
    fontWeight: 600,
  },
  time: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));
